import { jwtDecode } from "jwt-decode";

const NAMEID_CLAIM = "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier";
const NAME_CLAIM = "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name";

const asBool = (v) => v === true || v === "True" || v === "true";

function buildProfileFromJwt(decoded) {
  if (!decoded) return null;
  return {
    id: decoded[NAMEID_CLAIM] ?? decoded["nameid"] ?? null,
    userName: decoded[NAME_CLAIM] ?? decoded["unique_name"] ?? null,
    email: decoded["email"] ?? null,
    displayName: decoded["display_name"] ?? null,
    isAdministrator: asBool(decoded["is_admin"]),
    canDeployAll: asBool(decoded["can_deploy_all"]),
    canUseTelegramBot: asBool(decoded["can_use_telegram_bot"]),
    canViewAllDeployments: asBool(decoded["can_view_all_deployments"]),
    canSelectDeploymentNotificationRecipients: asBool(decoded["can_select_deployment_notification_recipients"]),
    hasCompletedFirstAccess: asBool(decoded["has_completed_first_access"]),
    isActive: true,
  };
}

export function mapUserToProfile(user) {
  if (!user) return null;
  return {
    id: user.id ?? null,
    userName: user.userName ?? null,
    email: user.email ?? null,
    displayName: user.displayName ?? null,
    isAdministrator: !!user.isAdministrator,
    canDeployAll: !!user.canDeployAll,
    canUseTelegramBot: !!user.canUseTelegramBot,
    canViewAllDeployments: !!user.canViewAllDeployments,
    canSelectDeploymentNotificationRecipients: !!user.canSelectDeploymentNotificationRecipients,
    isActive: user.isActive !== false,
    hasCompletedFirstAccess: !!user.hasCompletedFirstAccess,
    telegramPhoneNumber: user.telegramPhoneNumber ?? null,
    telegramChatId: user.telegramChatId ?? null,
    telegramUserName: user.telegramUserName ?? null,
    telegramLinkedAt: user.telegramLinkedAt ?? null,
    whatsappPhoneNumber: user.whatsappPhoneNumber ?? null,
    notifyEmailOnDeploymentCompletion: !!user.notifyEmailOnDeploymentCompletion,
    notifyTelegramOnDeploymentCompletion: !!user.notifyTelegramOnDeploymentCompletion,
    notifyWhatsappOnDeploymentCompletion: !!user.notifyWhatsappOnDeploymentCompletion,
  };
}

function clearStoredTokens() {
  localStorage.removeItem("jwt");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("refresh_token_expires_at");
}

function Reducer(state, action) {
  if (Array.isArray(action)) {
    action.forEach((x) => {
      state = Reducer(state, x);
    });
    return state;
  }

  switch (action.type) {
    case "SET_READY":
      return { ...state, ready: action.payload };
    case "SET_PROFILE":
      return { ...state, profile: mapUserToProfile(action.payload) };
    case "SET_LOGGED": {
      const payload = action.payload || {};
      const token = payload.accessToken ?? payload.token ?? null;

      if (!token) {
        clearStoredTokens();
        return {
          ...state,
          jwtDecoded: null,
          isLogged: false,
          jwt: null,
          refreshToken: null,
          refreshTokenExpiresAt: null,
          profile: null,
        };
      }

      let jwtDecoded = null;
      try {
        jwtDecoded = jwtDecode(token);
      } catch {
        clearStoredTokens();
        return {
          ...state,
          jwtDecoded: null,
          isLogged: false,
          jwt: null,
          refreshToken: null,
          refreshTokenExpiresAt: null,
          profile: null,
        };
      }

      const nextRefreshToken = payload.refreshToken ?? state.refreshToken ?? null;
      const nextRefreshTokenExpiresAt = payload.refreshTokenExpiresAt ?? state.refreshTokenExpiresAt ?? null;

      const user = payload.user;
      const profile = user
        ? mapUserToProfile(user)
        : state.profile ?? buildProfileFromJwt(jwtDecoded);

      localStorage.setItem("jwt", token);
      if (nextRefreshToken) localStorage.setItem("refresh_token", nextRefreshToken);
      if (nextRefreshTokenExpiresAt) localStorage.setItem("refresh_token_expires_at", nextRefreshTokenExpiresAt);

      return {
        ...state,
        jwtDecoded,
        isLogged: true,
        jwt: token,
        refreshToken: nextRefreshToken,
        refreshTokenExpiresAt: nextRefreshTokenExpiresAt,
        profile,
      };
    }
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    default:
      return state;
  }
}

export default Reducer;
